import React from 'react';
import { type ApolloError } from '@apollo/client';
import { Trans, useLingui } from '@lingui/react';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Sheet } from '@startuptools/ui/Sheet';
import { Alert } from '@startuptools/ui/Alert';
import { Table } from '@startuptools/ui/Table';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useCompanyQuery } from '../../../graphql/react-operations';
import { type I18n } from '@lingui/core';
import { type GqlShareClass } from '../../../graphql/base-types.graphql';

const renderContent = (loading: boolean, error: ApolloError | undefined, shareClasses: GqlShareClass[], i18n: I18n) => {
  if (loading) return null;

  if (error) {
    return (
      <Alert variant="outlined" color="danger">
        <Trans id="An error occurred. Please refresh the page. If the error persists, contact our customer support" />
      </Alert>
    );
  }

  if (shareClasses.length === 0) {
    return (
      <Alert variant="outlined" color="neutral">
        <Trans id="The company has no share classes registered with Bolagsverket yet." />
      </Alert>
    );
  }

  return (
    <Table>
      <thead>
        <tr>
          <th>
            <Trans id="Name" />
          </th>
          <th align="right">
            <Trans id="Quantity" />
          </th>
          <th align="right">
            <Trans id="Votes per Share" />
          </th>
        </tr>
      </thead>
      <tbody>
        {shareClasses.map(shareClass => (
          <tr key={shareClass.name}>
            <th>{shareClass.name}</th>
            <td align="right">
              <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                {shareClass.quantity}
              </NumberFormat>
            </td>
            <td align="right">
              <NumberFormat locale={i18n.locale} minimumFractionDigits={0} maximumFractionDigits={6}>
                {shareClass.voteWeight}
              </NumberFormat>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export const ShareClassesTable: React.FC = () => {
  const { i18n } = useLingui();
  const companyId = useCompanyId();
  const { data, error, loading } = useCompanyQuery({ variables: { id: companyId }, fetchPolicy: 'no-cache' });
  const shareClasses = data?.company.shareClasses || [];

  const content = renderContent(loading, error, shareClasses, i18n);

  return (
    <Stack gap={2}>
      <Typography level="h2">
        <Trans id="Share Class" />
      </Typography>
      {content && content.type === Table ? <Sheet variant="outlined">{content}</Sheet> : content}
    </Stack>
  );
};
